import React from "react";
import { useTranslation } from "react-i18next";
import { Box, Grid2 } from "@mui/material";
import { MyTimeAdmissionCard } from "my-time/components";
import {
  MyTimeApprovalUserClose,
  MyTimeEmployeeRequestsCard,
  MyTimeExaminersCard,
  MyTimeOvertimeFrame,
  MyTimePageHeader,
  MyTimeRemainsFrame,
  MyTimeWorkTimeBalanceFrameBox,
  MyTimeWorkingTimeFrame,
} from "my-time/components";
import {
  useMyTimeActualDate,
  useMyTimeExaminers,
  useMyTimeMonthlyWorkTimeBalance,
} from "my-time/hooks";
import { OcPageNameTitle, OcPageTransition } from "oc/components";
import { useOcCurrentUserState, useOcIsMobile, useOcProperty } from "oc/hooks";

export const MyTimeEmployeeSchedulePage = () => {
  const isMobile = useOcIsMobile();
  const { t, i18n } = useTranslation();
  const { contractCode, employeeCode } = useOcCurrentUserState();

  const { date, formattedDate, year, month, handleDateChange } =
    useMyTimeActualDate();

  const { data: changeMyTimeEnabledData } = useOcProperty({
    resource: "myTime",
    key: "changeMyTimeEnabled",
  });

  const { data, loading, error } = useMyTimeExaminers({
    contractCode,
    locale: i18n.language,
  });

  const { data: changeWorkingTimeEnabledData } = useOcProperty({
    resource: "myTime",
    key: "changeWorkingTimeEnabled",
  });

  const { data: userApprovalEnabledString } = useOcProperty({
    resource: "myTime",
    key: "userApprovalEnabled",
  });

  const { data: showBalanceFrameEnabledString } = useOcProperty({
    resource: "myTime",
    key: "showBalanceFrameEnabled",
  });

  const userApprovalEnabled =
    userApprovalEnabledString === "true" ? true : false;
  const showBalanceFrameEnabled =
    showBalanceFrameEnabledString === "true" ? true : false;

  const changeMyTimeEnabled = changeMyTimeEnabledData === "true";
  const changeWorkingTimeEnabled = changeWorkingTimeEnabledData === "true";
  const isNotExaminer = data?.length === 0;

  const {
    data: monthlyWorkTimeBalanceData,
    loading: monthlyWorkTimeBalanceLoading,
  } = useMyTimeMonthlyWorkTimeBalance({
    contractCode,
    year,
    month,
  });

  const minHeight = monthlyWorkTimeBalanceData?.startBalance !== -1 ? 285 : 178;
  const Grid2ItemStyle = {
    padding: isMobile ? "0px 16px" : "0px",
  };

  const [allTimesNull, setAllTimesNull] = React.useState<boolean>(false);

  return (
    <OcPageTransition>
      <OcPageNameTitle
        title={t("MY_TIME_EMPLOYEE_SCHEDULE_PAGE_TITLE")}
        goBack={false}
      />
      <Box padding={isMobile ? "0px 0px 16px 0px" : "0px 16px 16px 16px"}>
        <MyTimePageHeader
          formattedDate={formattedDate}
          date={date}
          onDateChange={handleDateChange}
          isMobile={isMobile}
        />
        <Box padding="16px 0px">
          <Grid2 container spacing={2} direction={isMobile ? "column" : "row"}>
            <Grid2 size={{ xs: 12, sm: 6, md: 3 }} style={Grid2ItemStyle}>
              <MyTimeWorkingTimeFrame
                contractCode={contractCode}
                year={year}
                month={month}
                minHeight={minHeight}
              />
            </Grid2>
            {showBalanceFrameEnabled && (
              <Grid2 size={{ xs: 12, sm: 6, md: 3 }} style={Grid2ItemStyle}>
                <MyTimeRemainsFrame
                  contractCode={contractCode}
                  year={year}
                  month={month}
                  minHeight={minHeight}
                />
              </Grid2>
            )}
            <Grid2 size={{ xs: 12, sm: 6, md: 3 }} style={Grid2ItemStyle}>
              <MyTimeOvertimeFrame
                contractCode={contractCode}
                year={year}
                month={month}
                minHeight={minHeight}
              />
            </Grid2>
            {monthlyWorkTimeBalanceData?.startBalance !== -1 && (
              <Grid2 size={{ xs: 12, sm: 6, md: 3 }} style={Grid2ItemStyle}>
                {isMobile && <Box height="16px" />}
                <MyTimeWorkTimeBalanceFrameBox
                  data={monthlyWorkTimeBalanceData}
                  loading={monthlyWorkTimeBalanceLoading}
                  minHeight={minHeight}
                />
              </Grid2>
            )}
            {!showBalanceFrameEnabled && monthlyWorkTimeBalanceData?.startBalance === -1 && (
              <Grid2 size={{ xs: 12, sm: 6, md: 3 }} style={Grid2ItemStyle}>
                {/* Empty spacer to keep Examiners card on the right */}
              </Grid2>
            )}
            <Grid2
              size={{ xs: 12, sm: 6, md: 3 }}
              style={{ maxWidth: isMobile ? "100%" : "25%" }}
            >
              <MyTimeExaminersCard
                data={data}
                loading={loading}
                error={error ? error.message : undefined}
              />
            </Grid2>
          </Grid2>
          {userApprovalEnabled && (
            <MyTimeApprovalUserClose
              formattedDate={formattedDate}
              allTimesNull={allTimesNull}
            />
          )}
          {changeMyTimeEnabled ? (
            <Box marginY="16px">
              <MyTimeEmployeeRequestsCard
                label={t("MY_TIME_MY_ADMISSION")}
                contractCode={contractCode}
                employeeCode={employeeCode}
                date={date}
                examinerView={false}
                showApproveAllButton={false}
              />
            </Box>
          ) : (
            <Box height={"16px"} />
          )}
          <MyTimeAdmissionCard
            contractCode={contractCode}
            employeeCode={employeeCode}
            year={year}
            month={month}
            changeMyTimeEnabled={changeMyTimeEnabled}
            changeWorkingTimeEnabled={changeWorkingTimeEnabled}
            isNotExaminer={isNotExaminer}
            isMobile={isMobile}
            userApprovalEnabled={userApprovalEnabled}
            onAllTimesNullChange={setAllTimesNull}
          />
        </Box>
      </Box>
    </OcPageTransition>
  );
};

export default MyTimeEmployeeSchedulePage;
